import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from "react";
import { ROUTES } from "constants/routes-path";
import {
    Button,
    ButtonGroup,
    Divider,
    Flex,
    Grid,
    Stack,
    Text, useColorModeValue
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { BiPrinter } from "react-icons/bi";
import CardHeader from "components/Card/CardHeader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { queryService } from 'services/queryService';
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Loading from "components/Loading/Loading";

const QueriesVehicleResult = ({searchByPlate, searchByChassis}) => {
    const [searchParams] = useSearchParams();
    const [response, setResponse] = useState(undefined);
    const [queryError, setQueryError] = useState(undefined);
    const showToast = useCustomToast();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const textColor = useColorModeValue("gray.700", "white");
    const errorCardBg = useColorModeValue("red.50", "whiteAlpha.100");
    const errorBorderColor = useColorModeValue("red.200", "red.400");
    const errorColor = useColorModeValue("red.600", "red.200");
    const translationKey = "queries";
    const formTranslationKey = `${translationKey}.form`;

    const ids = useMemo(
        () => ({
            ownerName: "nomeProprietario",
            ownerCpfCnpj: "cpfCnpjProprietario",
            vehiclePlate: "placa",
            plateUF: "ufPlaca",
            vehicleChassis: "numeroChassi",
            vehicleChassisRemarking: "remarcacaoChassi",
            vehicleStatus: "statusVeiculo",
            vehicleCategory: "descricaoCategoria",
            vehicleSpecies: "descricaoEspecie",
            vehicleType: "descricaoTipo",
            vehicleBrandModel: "descricaoMarcaModelo",
            vehicleColor: "descricaoCor",
            vehicleManufacturingYear: "anoFabricacao",
            vehicleModelYear: "anoModelo",
            vehicleOperationalYear: "anoExercicio",
            vehicleBody: "descricaoCarroceria",
            vehicleFuel: "descricaoCombustivel",
            vehicleRenavam: "numeroRenavam",
            cidadeImovelProprietario: "cidadeImovelProprietario",
        }),
        []
    );
    
    const fetchData = async () => {
        if (response) setResponse(undefined);
        setQueryError(undefined);

        try {
            if (searchByPlate) {
                const data = await queryService.findVehicleByPlate(searchParams.get("valor"));
                setResponse(data);
            } else if (searchByChassis) {
                const data = await queryService.findVehicleByChassis(searchParams.get("valor"));
                setResponse(data);
            }
            
        }
        catch (err) {
            showToast({ title: t(err), status: "error" });
            setQueryError(err);
        }
    };

    const redirectToQueryVehicle = () => {
        navigate(
            ROUTES.transportManagementPath + ROUTES.searches + ROUTES.searchVehicle,
            queryError
                ? {
                    state: {
                        failedVehicleQuery: {
                            type: searchByPlate ? "plate" : "chassis",
                            value: searchParams.get("valor") ?? "",
                        },
                    },
                }
                : undefined
        )
    };

    const handlePrintNoRecord = async () => {
        try {
            const plate = searchParams.get("valor");
            const url = ROUTES.getNoRecordPath.replace(":plate", plate);
            window.open(url, '_blank');
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    }

    const handlePrintSne = async () => {
        try {
            const plate = searchParams.get("valor");
            const url = ROUTES.getSnePath.replace(":plate", plate);
            window.open(url, '_blank');
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    }

    useEffect(() => {
        fetchData();
    },[]);

    return (
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card px="0px" mb="24px">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${translationKey}.title.query-vehicles`)}
                        </Text>
                    </Flex>
                </CardHeader>
                <CardBody px="22px">
                    { response ? (
                        <>
                            <Flex justify="center" mb={6}>
                                <ButtonGroup spacing="2" size="sm">
                                    <Button
                                        variant="light"
                                        fontSize="xs"
                                        leftIcon={<BiPrinter/>}
                                        onClick={() => handlePrintNoRecord()}
                                    >
                                        {t(`${translationKey}.actions.print-clearance-certificate`)}
                                    </Button>
                                    <Button
                                        variant="light"
                                        fontSize="xs"
                                        leftIcon={<BiPrinter/>}
                                        onClick={() => handlePrintSne()}
                                    >
                                        {t(`${translationKey}.actions.print-sne-status`)}
                                    </Button>
                                </ButtonGroup>
                            </Flex>
                            <Card border="1px solid #3884cc" bg={cardBg}>
                                <CardHeader>
                                    <Text color={textColor} fontSize="md" fontWeight="bold" >
                                        {t(`${formTranslationKey}.title.vehicle-data`)}
                                    </Text>
                                </CardHeader>
                                <Divider borderColor="#3884cc" mb="20px" />
                                <CardBody>
                                    <Stack spacing={4}>
                                        <Text color={textColor} fontSize="md">
                                            <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.owner-name.label`)}:
                                            </Text>{"\u00A0"}
                                            {response[ids.ownerName] ?? "-"}
                                        </Text>
                                        <Grid templateColumns="repeat(3, 1fr)" gap="10px">
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.owner-cpf-cnpj.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.ownerCpfCnpj] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-plate.label`)}
                                                </Text>{"\u00A0"}
                                                {response[ids.vehiclePlate] ?? "-"}
                                            </Text>
                                             <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.renavam-number.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleRenavam] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-plate-uf.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.plateUF] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-chassis.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleChassis] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-chassis-remarking.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleChassisRemarking] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-status.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleStatus] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-category.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleCategory] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-species.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleSpecies] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-type.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleType] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-brand-model.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleBrandModel] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-color.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleColor] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-manufacturing-year.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleManufacturingYear] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-model-year.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleModelYear] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-operational-year.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleOperationalYear] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-body.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleBody] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-fuel.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.vehicleFuel] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.municipality.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.cidadeImovelProprietario] ?? "-"}
                                            </Text>
                                        </Grid>
                                    </Stack>
                                </CardBody>
                            </Card>
                        </>
                    ) : queryError ? (
                        <Card
                            role="alert"
                            bg={errorCardBg}
                            border="1px solid"
                            borderColor={errorBorderColor}
                            borderRadius="md"
                            p="20px"
                        >
                            <Flex align="flex-start" gap="12px">
                                <WarningTwoIcon color={errorColor} boxSize="20px" mt="2px" />
                                <Stack spacing="4px">
                                    <Text color={errorColor} fontSize="md" fontWeight="bold">
                                        {t(`${translationKey}.messages.errors.vehicle-query-title`)}
                                    </Text>
                                    <Text color={textColor} fontSize="sm">
                                        {t(`${translationKey}.messages.errors.vehicle-query-description`)}
                                    </Text>
                                    <Text color={textColor} fontSize="sm">
                                        <Text as="span" fontWeight="bold">
                                            {t(`${translationKey}.messages.errors.vehicle-query-detail`)}:
                                        </Text>{" "}
                                        {t(queryError)}
                                    </Text>
                                </Stack>
                            </Flex>
                            <Flex justify="center" mt="20px">
                                <Button
                                    variant="dark"
                                    fontSize="sm"
                                    w="160px"
                                    h="40px"
                                    onClick={fetchData}
                                >
                                    {t(`${translationKey}.actions.retry`)}
                                </Button>
                            </Flex>
                        </Card>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                    <Flex justifyContent="flex-start">
                        <Button
                            variant="light"
                            fontSize="sm"
                            alignSelf="flex-end"
                            mt="24px"
                            w="125px"
                            h="40px"
                            onClick={() => redirectToQueryVehicle()}
                        >
                            {t("system.actions.back")}
                        </Button>
                    </Flex>
                </CardBody>
            </Card>
        </Flex >
    );

};

export default QueriesVehicleResult;
